import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import ExtensionCommentThread from './ExtensionCommentThread';
import * as styles from './ExtensionMagneticWall.module.scss';
import type { MagneticWallType } from '../../../editor/editorSlice';
import useSyncMagneticWallStates from '../../../editor/useSyncMagneticWallState';
import type { DocumentType } from '../../../graphql/generated/gql';
import { gql } from '../../../graphql/generated/gql';
import type { ExtensionCommentThread_ThreadFragment } from '../../../graphql/generated/graphql';
import useVisibleMagneticWallItems from '../../../post/useVisibleMagneticWallItems';
import { isCommentOrPublicCommentThread } from '../../../utils/post/threadUtils';

const AvatarHeight = 40;

const POST_FRAGMENT = gql(`
  fragment ExtensionMagneticWall_post on Post {
    threads {
      id
      ...ExtensionCommentThread_thread
    }
    ...ExtensionCommentThread_post
  }
`);

interface MagneticWallProps {
  quill: Quill | null;
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ExtensionMagneticWall = ({ quill, post }: MagneticWallProps) => {
  const [activeThread, setActiveThread] = useState<{
    id: string;
    type: MagneticWallType;
  } | null>(null);
  const [positions, setPositions] = useState<
    {
      thread: ExtensionCommentThread_ThreadFragment;
      node: Element;
      top: number;
    }[]
  >([]);

  const threads = useMemo(
    () =>
      post.threads.filter((thread) => isCommentOrPublicCommentThread(thread)),
    [post.threads],
  );

  useSyncMagneticWallStates({
    magneticWallHighlight: null,
    magneticWallFocus:
      activeThread == null
        ? null
        : {
            id: activeThread.id,
            type: activeThread.type,
            active: true,
            hasExpandIntent: false,
          },
  });

  useVisibleMagneticWallItems(
    threads
      .filter((thread) => positions.some((p) => thread.id === p.thread.id))
      .map((thread) => ({ id: thread.id, type: 'comment' })),
  );

  const updatePositions = useCallback(
    (localQuill: Quill) => {
      if (!localQuill) {
        setPositions([]);
        return;
      }

      const container = localQuill.root;

      const containerTop = container.getBoundingClientRect().top;

      const newPositions = _.compact(
        threads.map((thread) => {
          const node = container.querySelector(`.comment-${thread.id}`);
          if (!node) return null;
          if (!(node instanceof HTMLElement) || node.offsetParent === null) {
            return null;
          }
          const rect = node.getBoundingClientRect();
          return { node, thread, top: rect.top - containerTop };
        }),
      ).toSorted((a, b) => a.top - b.top);

      setPositions(
        newPositions.map((position, i) => {
          const prevPosition = newPositions[i - 1];
          return prevPosition
            ? {
                ...position,
                top: Math.max(
                  position.top,
                  Number(prevPosition.top) + AvatarHeight,
                ),
              }
            : position;
        }),
      );
    },
    [threads],
  );

  const debouncedUpdatePositions = useDebouncedCallback(updatePositions, 200);

  useEffect(() => {
    if (!quill) return undefined;
    updatePositions(quill);
    debouncedUpdatePositions(quill);

    const listener = () => {
      debouncedUpdatePositions(quill);
    };

    quill.on('text-change', listener);
    return () => {
      quill.off('text-change', listener);
    };
  }, [updatePositions, debouncedUpdatePositions, quill]);

  useEffect(() => {
    if (!quill) return undefined;
    const listener = (e: MouseEvent) => {
      if (e.target instanceof Element) {
        const commentNode = e.target.closest('[class*="comment-"]');
        if (commentNode) {
          for (const c of commentNode.classList) {
            if (c.startsWith('comment-')) {
              setActiveThread({
                id: c.replace(/^comment-/, ''),
                type: 'comment',
              });
              return;
            }
          }
        }
      }
      setActiveThread(null);
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) return undefined;

    const observer = new ResizeObserver(() => debouncedUpdatePositions(quill));
    observer.observe(quill.root);

    return () => {
      observer.disconnect();
    };
  }, [debouncedUpdatePositions, quill]);

  return (
    <div className={styles.container}>
      {positions.map((position) => (
        <div
          key={position.thread.id}
          className={styles.comment}
          style={{ top: position.top }}
        >
          <ExtensionCommentThread
            thread={position.thread}
            post={post}
            activeThreadId={activeThread?.id ?? null}
            onActiveThreadIdChange={(id) =>
              setActiveThread(id ? { id, type: 'comment' } : null)
            }
          />
        </div>
      ))}
    </div>
  );
};

export default ExtensionMagneticWall;
